<template>
	<v-navigation-drawer v-if="isEditMode" permanent width="280" mobile-breakpoint="0" color="panel_sub" theme="dark"
		class="edit-panel">
		<!-- Header section -->
		<v-list-item class="edit-panel-header">
			<template v-slot:prepend>
				<!-- Dynamic icon rendering -->
				<template v-if="editConfig">
					<img v-if="editConfig.icon.type === 'svg'" class="mr-2" :src="editConfig.icon.value" :alt="displayName"
						width="24" height="24" />
				</template>
			</template>
			<template v-slot:title>
				<div class="d-flex align-center">
					<!-- The mdi icon is drawn here to prevent the prepend slot from generating an automatic spacer -->
					<v-icon v-if="editConfig && editConfig.icon.type === 'mdi'" class="mr-2" size="24" style="opacity: 1;">{{
						editConfig.icon.value
					}}</v-icon>
					<!-- Display name when not renaming -->
					<ListItemLabel v-if="!isRenamingMode" :text="displayName" placement="bottom-start" />
					<!-- Dynamic rename component -->
					<component v-if="isRenamingMode && editConfig" :is="editConfig.renameComponent"
						v-bind="editConfig.getRenameProps(editTarget)" @end-rename="isRenamingMode = false"
						:is-renaming-mode="isRenamingMode" class="flex-grow-1 object-name-wrapper">
					</component>
					<v-btn v-if="!isRenamingMode" variant="text" icon size="22" @click="isRenamingMode = true" class="rename-btn">
						<v-icon size="20">mdi-rename</v-icon>
					</v-btn>
				</div>
				<div class="object-subtitle" v-if="objectUnderEdit && associatedTerrainName">
					<ListItemLabel :text="associatedTerrainName" placement="bottom-start" />
				</div>
			</template>
		</v-list-item>

		<!-- Action section -->
		<div v-if="editConfig && editTarget" class="edit-panel-action">
			<v-btn color="primary" rounded block @click="endEdit" class="end-edit-btn" :disabled="!effectiveFormValid">
				<template v-slot:prepend>
					<v-icon>mdi-check</v-icon>
				</template>
				{{ $t("FINISH_EDITING") }}
			</v-btn>
		</div>

		<!-- Content section -->
		<div v-if="editConfig && editTarget" class="edit-panel-content">
			<v-card variant="flat" color="panel_main">
				<component :is="editConfig.component" v-bind="editConfig.getProps(editTarget)"
					@update:valid="isFormValid = $event">
				</component>
			</v-card>
		</div>
	</v-navigation-drawer>
</template>

<script lang="ts">
import DiggingIcon from "@/assets/icons/customIcons/digging-icon.svg";
import FlatFieldIcon from "@/assets/icons/customIcons/flat-field-icon.svg";
import TemporaryRoadIcon from "@/assets/icons/customIcons/temporary-road-icon.svg";
import { SIMPLE_STRUCTURE_TYPE } from "@/models/objects/solidMesh/types";
import { ObjectType } from "@/models/objects/types";
import validationRules from "@/utils/validation-rules.js";
import Drawing2dImageListItem from "./Drawing2dImageListItem.vue";
import Drawing2dImageName from "./Drawing2dImageName.vue";
import ListItemLabel from "./ListItemLabel.vue";
import ObjectName from "./ObjectName.vue";
import FlatGroundPanel from "./ObjectPanels/FlatGroundPanel.vue";
import RoadPanel from "./ObjectPanels/RoadPanel.vue";
import TrenchPanel from "./ObjectPanels/TrenchPanel.vue";
import { getStructureTypeConfig } from "./SolidMesh/SimpleStructureTypeConfig";
import SolidMeshPanel from "./SolidMesh/SolidMeshPanel.vue";

export default {
	name: "EditPanel",
	components: {
		RoadPanel,
		FlatGroundPanel,
		TrenchPanel,
		SolidMeshPanel,
		ObjectName,
		Drawing2dImageName,
		ListItemLabel,
		Drawing2dImageListItem,
	},
	data() {
		return {
			ObjectType,
			TemporaryRoadIcon,
			FlatFieldIcon,
			DiggingIcon,
			isRenamingMode: false,
			isFormValid: true,
			rules: {
				...validationRules,
				required: (value) => !!value || "*",
				required0: (value) => value === 0 || !!value || "*",
				fgValidInterpolation: (v) => Number.parseFloat(v) > 0,
			},
		};
	},
	computed: {
		isEditMode() {
			// Do not show edit panel during 2-point alignment
			if (this.$store.state.drawing2dImage?.isTwoPointAlignment) {
				return false;
			}
			if (
				this.$store.state.inProgressModeManager?.objectType ===
				ObjectType.FOUNDATION_EXCAVATION
			) {
				return false;
			}
			return this.$store.state.inProgressModeManager?.inProgressMode === "EDIT";
		},
		imageUnderEdit() {
			return this.$store.state.drawing2dImage?.imageUnderEdit;
		},
		objectUnderEdit() {
			return this.$store.state.object?.objectUnderEdit;
		},
		editTypeConfig() {
			return {
				[ObjectType.ROAD]: {
					component: "RoadPanel",
					icon: { type: "svg", value: this.TemporaryRoadIcon },
					getProps: (obj) => ({ objId: obj.id, rules: this.rules }),
					getName: (obj) => obj.name,
					renameComponent: "ObjectName",
					getRenameProps: (obj) => ({ objId: obj.id }),
				},
				[ObjectType.FLAT_GROUND]: {
					component: "FlatGroundPanel",
					icon: { type: "svg", value: this.FlatFieldIcon },
					getProps: (obj) => ({ objId: obj.id, rules: this.rules }),
					getName: (obj) => obj.name,
					renameComponent: "ObjectName",
					getRenameProps: (obj) => ({ objId: obj.id }),
				},
				[ObjectType.TRENCH]: {
					component: "TrenchPanel",
					icon: { type: "svg", value: this.DiggingIcon },
					getProps: (obj) => ({ objId: obj.id, rules: this.rules }),
					getName: (obj) => obj.name,
					renameComponent: "ObjectName",
					getRenameProps: (obj) => ({ objId: obj.id }),
				},
				[ObjectType.SOLID_MESH]: {
					component: "SolidMeshPanel",
					icon: { type: "mdi", value: "mdi-drawing-box" },
					getProps: (obj) => ({ solidMeshId: obj.id, isEditing: true }),
					getName: (obj) => obj.name,
					renameComponent: "ObjectName",
					getRenameProps: (obj) => ({ objId: obj.id }),
				},
				drawing2dImage: {
					component: "Drawing2dImageListItem",
					icon: { type: "mdi", value: "mdi-drawing-box" },
					getProps: (image) => ({
						entity: image,
						draggable: false,
						isEditing: true,
					}),
					getName: (image) => image.getName(),
					renameComponent: "Drawing2dImageName",
					getRenameProps: (image) => ({ imageId: String(image.assetId) }),
				},
			};
		},
		editConfig() {
			if (this.objectUnderEdit && this.objectUnderEdit.type) {
				const baseConfig = this.editTypeConfig[this.objectUnderEdit.type];
				if (this.objectUnderEdit.type === ObjectType.SOLID_MESH) {
					return {
						...baseConfig,
						icon: {
							type: "mdi",
							value: this.getSolidMeshIcon(this.objectUnderEdit),
						},
					};
				}
				return baseConfig;
			} else if (this.imageUnderEdit) {
				return this.editTypeConfig.drawing2dImage;
			}
			return null;
		},
		editTarget() {
			return this.objectUnderEdit || this.imageUnderEdit;
		},
		displayName() {
			if (this.editConfig && this.editTarget) {
				return this.editConfig.getName(this.editTarget);
			}
			return "";
		},
		effectiveFormValid() {
			if (this.objectUnderEdit?.type === ObjectType.SOLID_MESH) {
				return this.$store.getters["object/isEditFormValid"];
			}
			return this.isFormValid;
		},
		associatedTerrainName() {
			if (!this.objectUnderEdit || !this.objectUnderEdit.associatedTerrain) {
				return "";
			}

			const terrain = this.$store.getters.terrainList.find(
				(t) => t.getAssetId() == this.objectUnderEdit.associatedTerrain.id,
			);

			if (!terrain) {
				return "";
			}

			const terrainTypeName = this.getTerrainTypeName(terrain.getType());
			return `${terrainTypeName}: ${terrain.getName()}`;
		},
	},
	watch: {
		objectUnderEdit: {
			handler(newObj) {
				if (newObj) {
					newObj.isOpen = true;
				}
			},
			immediate: true,
		},
		isFormValid(newVal) {
			this.$store.commit("object/setEditFormValid", newVal);
		},
	},
	methods: {
		async endEdit() {
			try {
				const currentObjectUnderEdit = this.objectUnderEdit;
				let ended = false;
				await this.$store.dispatch("executeWithSpinner", async () => {
					ended = await this.$store.dispatch("inProgressModeManager/endEdit");
				});
				if (ended && currentObjectUnderEdit) {
					currentObjectUnderEdit.isOpen = false;
				}
			} catch (error) {
				console.error("Error ending edit:", error);
			}
		},
		getTerrainTypeName(type) {
			const typeMap = {
				pointcloud: this.$t("POINT_CLOUD"),
				design: this.$t("DESIGN"),
				"reference-topology": this.$t("REFERENCE_TOPOLOGY"),
			};
			return typeMap[type] || type;
		},
		getSolidMeshIcon(obj) {
			const structureType =
				obj?.metaData?.attributes?.structureType ??
				obj?.attributes?.structureType ??
				SIMPLE_STRUCTURE_TYPE.MANUAL;
			return getStructureTypeConfig(structureType)?.icon ?? "mdi-drawing-box";
		},
	},
};
</script>

<style scoped>
.edit-panel {
	position: relative;
	height: 100%;
	flex-shrink: 0;
}

.edit-panel-header {
	height: 56px;
	background-color: var(--v-theme-panel_sub);
}

.object-name-wrapper {
	min-width: 0;
}

.object-name-wrapper :deep(.obj-name) {
	font-size: 18px;
	font-weight: 500;
	line-height: 1.2;
}

.rename-btn {
	flex-shrink: 0;
}

.rename-btn:hover {
	opacity: 1;
}

.object-subtitle {
	font-size: 12px;
	color: rgba(255, 255, 255, 0.7);
	margin-top: 2px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.edit-panel-action {
	padding: 16px;
	background-color: #29292f;
	position: relative;
	z-index: 10;
	box-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
}

.edit-panel-content {
	position: absolute;
	top: 128px;
	bottom: 0;
	overflow-y: auto;
	padding-bottom: 16px;
	background-color: #29292f;
}

.end-edit-btn {
	height: 40px;
	font-size: 14px;
}
</style>
